import { useCallback } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { useRxDB } from '../core/useRxDB'
import type { BaseDocument } from '../core/types'
import { spaceOrderId } from '@/lib/utils/spaceOrderId'
import { logger } from '@/lib/monitoring/logger'
import useLoginStore from '@/store/LoginStore'

/**
 * SpaceOrder document interface.
 * One row = "this user puts this root Space at this LexoRank".
 * Backend domain name: UserProjectOrder.
 */
export interface SpaceOrderDocument extends BaseDocument {
  userId: string
  projectId: string
  sortOrder: string
}

const hooks = createEntityHooks<SpaceOrderDocument>('spaceorders')

const MAX_WRITE_RETRIES = 3

/**
 * The current user's private Space ordering. Empty until their first reorder —
 * callers fall back to A–Z (see sortSpacesByPrivateOrder).
 */
export const useSpaceOrders = () => {
  const envId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const enabled = !!(envId && userId)

  const { data, isLoading, db } = hooks.useEntities(
    enabled ? { envId, userId, isDeleted: false } : undefined,
    { enabled },
  )

  return { spaceOrders: data, isLoading, db }
}

export const useSpaceOrderMutations = () => {
  const { db } = useRxDB()
  const envId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)

  /**
   * Upserts this user's rank for a Space.
   *
   * Keyed on the deterministic UUIDv5 id rather than a query, so two devices
   * ranking the same Space offline write the same row instead of racing the
   * backend's filtered unique index. A row retired by a Space deletion is
   * resurrected in place for the same reason — re-inserting would collide with
   * the local document that still holds that id.
   */
  const setSpaceRank = useCallback(
    async (projectId: string, sortOrder: string): Promise<void> => {
      if (!db || !envId || !userId) {
        throw new Error('Database not initialized or user not logged in')
      }

      const id = spaceOrderId(envId, userId, projectId)
      const collection = (db as any).spaceorders

      for (let attempt = 0; attempt < MAX_WRITE_RETRIES; attempt++) {
        try {
          const existing = await collection.findOne(id).exec()
          const now = Date.now()

          if (existing) {
            await existing.update({
              $set: {
                sortOrder,
                isDeleted: false,
                deletedOn: null,
                deletedBy: null,
                updatedOn: now,
                updatedBy: userId,
              },
            })
            return
          }

          await collection.insert({
            id,
            envId,
            userId,
            projectId,
            sortOrder,
            isDeleted: false,
            createdOn: now,
            createdBy: userId,
            updatedOn: now,
            updatedBy: userId,
            deletedOn: null,
            deletedBy: null,
          })
          return
        } catch (error: any) {
          // A materialise pass writes every visible Space in a loop, so a stale
          // revision here just means a sibling write landed first — re-read and
          // retry rather than dropping the user's drag on the floor.
          if (error?.code === 'CONFLICT' && attempt < MAX_WRITE_RETRIES - 1) {
            continue
          }
          logger.logError(error as Error, 'DATABASE', {
            collection: 'spaceorders',
            entityId: id,
            operation: 'set_space_rank',
          })
          throw error
        }
      }
    },
    [db, envId, userId],
  )

  const { update, delete: deleteEntity } = hooks.useMutations()

  return {
    setSpaceRank,
    updateSpaceOrder: update,
    deleteSpaceOrder: deleteEntity,
  }
}
